import React from 'react';
import type { GraphQLTaggedNode } from 'react-relay';
import { graphql, useFragment } from 'react-relay';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { getContentMode } from '@confluence/content-types-utils/entry-points/getContentMode.ts';
import {
	VALID_OPERATION_TARGETS,
	type ContentType,
} from '@confluence/content-types-utils/entry-points/contentTypeConstants.ts';
import { ContentTypeClassificationLoader } from '@confluence/data-classification/entry-points/ContentTypeClassification.tsx';
import { ClassificationEditModal } from '@confluence/data-classification/entry-points/ClassificationEditModal.ts';
import { TagButtonUIVariant } from '@confluence/data-classification/entry-points/tagButtonUIVariant.ts';
import {
	useContentClassification,
	type ClassificationQueryContext,
} from '@confluence/data-classification/entry-points/queries.ts';
import type { ContentClassificationMetadata } from '@confluence/data-classification/entry-points/hooks.ts';
import { useContentClassificationFromFragment } from '@confluence/data-classification/entry-points/useContentClassificationFromFragment.ts';
import type { useContentClassification_content$key } from '@confluence/data-classification/entry-points/useContentClassificationFragmentType.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { useIsRestrictedExternalShareRequest } from '@confluence/external-share-utils/entry-points/useIsRestrictedExternalShareRequest.ts';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { expVal } from '@confluence/feature-experiments';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

import type { BylineClassification_content$key } from './__generated__/BylineClassification_content.graphql.ts';
import type {
	BylineClassificationQuery,
	BylineClassificationQueryVariables,
} from './__types__/BylineClassificationQuery.ts';

export const BylineClassificationContentFragment: GraphQLTaggedNode = graphql`
	fragment BylineClassification_content on Content
	@argumentDefinitions(useClassificationLevelV2: { type: "Boolean!", defaultValue: false }) {
		id
		title
		type
		status
		operations {
			operation
			targetType
		}
		space {
			id
			name
			key
			alias
		}
		...useContentClassification_content
			@arguments(useClassificationLevelV2: $useClassificationLevelV2)
	}
`;

type BylineClassificationContentData = {
	readonly type?: string | null;
	readonly status?: string | null;
	readonly operations?: ReadonlyArray<{
		readonly operation?: string | null;
		readonly targetType?: string | null;
	} | null> | null;
	readonly space?: {
		readonly key?: string | null;
	} | null;
};

type BylineClassificationProps = {
	contentId: string;
	classificationContextFragmentRef?: BylineClassification_content$key | null;
	contentFragmentRef?: useContentClassification_content$key | null;
};

export const BylineClassification = ({
	contentId,
	classificationContextFragmentRef,
	contentFragmentRef,
}: BylineClassificationProps): React.JSX.Element | null => {
	if (
		classificationContextFragmentRef &&
		!StatsigConfigurations.isKillSwitchOn('confluence_vp_classification_context_frag_reader') &&
		expVal('confluence_exp_vp_document', 'isEnabled', false)
	) {
		return (
			<BylineClassificationContextFragmentReader
				contentId={contentId}
				contentFragmentRef={classificationContextFragmentRef}
			/>
		);
	}

	return (
		<BylineClassificationLegacyQueryReader
			contentId={contentId}
			contentFragmentRef={contentFragmentRef}
		/>
	);
};

const BylineClassificationContextFragmentReader = ({
	contentId,
	contentFragmentRef,
}: {
	contentId: string;
	contentFragmentRef: BylineClassification_content$key;
}): React.JSX.Element | null => {
	const contentData = useFragment(BylineClassificationContentFragment, contentFragmentRef);
	const readerProps = useBylineClassificationReaderProps(contentId, contentData);
	const contentClassificationMetadata = useContentClassificationFromFragment({
		contentId,
		contentFragmentRef: contentData,
		contentStatusContext: readerProps.contentStatusContext,
		shouldReadFromQueries: true,
	});

	return (
		<BylineClassificationContent
			{...readerProps}
			contentClassificationMetadata={contentClassificationMetadata}
		/>
	);
};

const BylineClassificationLegacyQueryReader = ({
	contentId,
	contentFragmentRef,
}: {
	contentId: string;
	contentFragmentRef?: useContentClassification_content$key | null;
}): React.JSX.Element | null => {
	// Reuse the content-types header query since it will already be cached on the legacy path.
	// eslint-disable-next-line graphql-client-usage/graphql-generic-types
	const { data, error: bylineClassificationQueryError } = useQueryInterop<
		BylineClassificationQuery,
		BylineClassificationQueryVariables
	>(
		graphql`
			query BylineClassificationQuery($contentId: ID) {
				content(id: $contentId, status: ["archived", "current", "draft"]) {
					nodes {
						id
						title
						operations {
							operation
							targetType
						}
						space {
							id
							name
							key
							alias
						}
						type
						status
					}
				}
			}
		`,
		{
			variables: { contentId },
		},
	);
	const contentData = data?.content?.nodes?.[0];
	const readerProps = useBylineClassificationReaderProps(contentId, contentData);

	if (bylineClassificationQueryError) {
		return <ErrorDisplay error={bylineClassificationQueryError} />;
	}

	if (
		contentFragmentRef &&
		!StatsigConfigurations.isKillSwitchOn('confluence_vp_meta_frag_reader') &&
		expVal('confluence_exp_vp_document', 'isEnabled', false)
	) {
		return (
			<BylineClassificationMetadataFragmentReader
				{...readerProps}
				contentFragmentRef={contentFragmentRef}
			/>
		);
	}

	return <BylineClassificationMetadataQueryReader {...readerProps} />;
};

type BylineClassificationReaderProps = {
	contentId: string;
	spaceKey: string;
	spaceKeyOverride?: string | null;
	contentData: BylineClassificationContentData | null | undefined;
	contentType: {
		type?: string | null;
		mode: string;
	};
	contentStatusContext: ClassificationQueryContext;
	isExternal: boolean;
	isViewModeReadOnly: boolean;
};

const useBylineClassificationReaderProps = (
	contentId: string,
	contentData: BylineClassificationContentData | null | undefined,
): BylineClassificationReaderProps => {
	const isExternal = useIsRestrictedExternalShareRequest();

	// Different pieces of content can have different modes, which need to be reflected in this header.
	// Eg, pages & blogposts can be 'draft', 'archived', 'view', 'edit', or 'live' (or 'embedded', or 'legacy'!)
	const rawContentType = contentData?.type ?? undefined;
	const isArchived = contentData?.status === 'archived';
	const isDraft = contentData?.status === 'draft';
	const isViewEditOrLive = useGetPageMode();
	const contentMode =
		getContentMode(rawContentType, isArchived, isDraft, isViewEditOrLive) ||
		isViewEditOrLive ||
		PageMode.VIEW;
	const contentType = {
		type: rawContentType,
		mode: contentMode,
	};

	let contentStatusContext: ClassificationQueryContext;
	if (
		contentType.mode === 'live' ||
		(contentType.mode === PageMode.VIEW &&
			expVal('dlp_auto_classifications_enabled', 'isEnabled', false))
	) {
		contentStatusContext = 'current-and-draft';
	} else if (contentType.mode === 'edit' || contentType.mode === 'draft') {
		contentStatusContext = 'draft-only';
	} else {
		contentStatusContext = 'current-only';
	}

	return {
		contentId,
		spaceKey: contentData?.space?.key || '',
		spaceKeyOverride: contentData?.space?.key,
		contentData,
		contentType,
		contentStatusContext,
		isExternal,
		isViewModeReadOnly:
			contentType.mode === PageMode.VIEW &&
			!expVal('dlp_auto_classifications_enabled', 'isEnabled', false),
	};
};

const BylineClassificationMetadataQueryReader = ({
	contentId,
	spaceKeyOverride,
	...props
}: BylineClassificationReaderProps): React.JSX.Element | null => {
	const contentClassificationMetadata = useContentClassification({
		contentId,
		spaceKeyOverride,
		contentStatusContext: props.contentStatusContext,
		shouldReadFromQueries: true,
	});

	return (
		<BylineClassificationContent
			contentId={contentId}
			contentClassificationMetadata={contentClassificationMetadata}
			{...props}
		/>
	);
};

const BylineClassificationMetadataFragmentReader = ({
	contentId,
	contentFragmentRef,
	...props
}: BylineClassificationReaderProps & {
	contentFragmentRef: useContentClassification_content$key;
}): React.JSX.Element | null => {
	const contentClassificationMetadata = useContentClassificationFromFragment({
		contentId,
		contentFragmentRef,
		contentStatusContext: props.contentStatusContext,
		shouldReadFromQueries: true,
	});

	return (
		<BylineClassificationContent
			contentId={contentId}
			contentClassificationMetadata={contentClassificationMetadata}
			{...props}
		/>
	);
};

const BylineClassificationContent = ({
	contentId,
	spaceKey,
	contentData,
	contentType,
	contentClassificationMetadata,
	isExternal,
	isViewModeReadOnly,
}: BylineClassificationReaderProps & {
	contentClassificationMetadata: ContentClassificationMetadata;
}): React.JSX.Element | null => {
	const hasUpdatePermission = expVal('dlp_auto_classifications_enabled', 'isEnabled', false)
		? (contentClassificationMetadata.isReclassificationPermitted ?? false)
		: contentData?.operations?.some(
				(operation) =>
					operation?.targetType &&
					VALID_OPERATION_TARGETS.includes(operation.targetType as ContentType) &&
					operation.operation === 'update',
			);
	const readOnly = isExternal || !hasUpdatePermission || isViewModeReadOnly;
	const isClassificationEnabled = contentClassificationMetadata.featureEnabled ?? false;

	return isClassificationEnabled ? (
		<>
			<ContentTypeClassificationLoader
				variant={TagButtonUIVariant.default}
				spaceKey={spaceKey}
				contentId={contentId}
				contentType={contentType.type ?? ''}
				contentClassificationMetadata={contentClassificationMetadata}
				readOnly={readOnly}
				spacing="expanded"
			/>
			<ClassificationEditModal
				contentId={contentId}
				spaceKey={spaceKey}
				contentClassificationMetadata={contentClassificationMetadata}
			/>
		</>
	) : null;
};
